import { Component, ReactNode } from 'react';

import classNames from 'classnames';
import { isInSelectionMode } from '../../store/productSelection/actions';
import historyUtils from '../utils/utils-routing';
import DisplayTypeDropDown from './DisplayTypeDropDown';
import GroupByDropDown from './GroupByDropDown';
import Pagination from './Pagination';
import { SortByDropDown } from './SortByDropDown';
import { AppDispatch } from '../../store';
import { DisplayTypeState } from '../../store/displayType/state';
import { ProductSelectionState } from '../../store/productSelection/state';
import { GroupingState } from '../../store/grouping/state';
import { ChangePriceDialog } from './ChangePriceDialog';
import { ButtonSize } from './Button';
import { PriceTier } from '../../../models/PriceTier';
import { BulkActionResponse } from '../../store/api';
import i18n from 'i18next';

type OwnProps = {
    readonly sorting?: ReadonlyArray<{ readonly key: string; readonly label: string }>;
    readonly excludeSorting?: ReadonlyArray<string>;
    readonly grouping?: GroupingState;
    readonly isFilterToggleReplaced?: boolean;
    readonly leftColumnText?: ReactNode;
    readonly hideSelectButtons?: boolean;
    readonly hideSelectAll?: boolean;
    readonly hidePagination?: boolean;
    readonly hideDisplayType?: boolean;
    readonly hideGroupBy?: boolean;
    readonly excludeDisplayTypes?: ReadonlyArray<DisplayTypeState>;
    readonly groupByGroups?: ReadonlyArray<{ readonly key: string; readonly label: string }>;
    readonly loading?: boolean;
    readonly className?: string;
    readonly selectAll?: () => void;
    readonly toggleSelectionMode?: () => void;
    readonly onPageChange?: (page: number) => void;
    readonly onGroupBy?: (groupBy: { readonly key: string; readonly label?: string }) => void;
    readonly onChangePrice?: (priceTier: PriceTier, availabilityCheck: boolean) => Promise<BulkActionResponse>;
};

type Props = {
    readonly isConnected?: boolean;
    readonly currentPage?: number;
    readonly pageSize?: number;
    readonly sortBy?: string;
    readonly totalProducts: number;
    //TODO replace productSelection with a boolean value
    readonly productSelection?: ProductSelectionState;
    readonly displayType?: DisplayTypeState;
    readonly displayFilter?: boolean;
};

type Actions = {
    readonly onSortChange: (sortBy: string) => void;
    readonly onDisplayChange?: (type: DisplayTypeState) => void;
    readonly onFilterDisplayToggle?: () => void;
    readonly dispatch: AppDispatch;
};

export type FilterBarProps = Props & OwnProps & Actions;

export class FilterBar extends Component<Props & Actions & OwnProps> {
    constructor(props) {
        super(props);

        this.toggleFilterDisplay = this.toggleFilterDisplay.bind(this);
    }

    toggleFilterDisplay() {
        historyUtils.replaceURLSearchState('displayFilter', this.props.displayFilter ? undefined : null, false);
        this.props.onFilterDisplayToggle?.();
    }

    render() {
        let leftColumn: ReactNode;

        if (this.props.isFilterToggleReplaced) {
            leftColumn = (
                <div className="layout-column-33 fl-left">
                    <span className="left-column-header">{this.props.leftColumnText}</span>
                </div>
            );
        } else {
            const toggleSelectionModeFunc = this.props.toggleSelectionMode;

            const selectAllFunc = this.props.selectAll;

            leftColumn = (
                <div className="layout-column-33 fl-left nowrap" data-testid="filter-bar">
                    <a onClick={this.toggleFilterDisplay} className="fl-left">
                        <i className="material-icons fl-left">list</i>
                        <div style={{ display: 'inline-block' }}> {i18n.t('components.filterBar.filter')}</div>
                    </a>
                    {this.props.totalProducts >= 0 ? (
                        <div className="total-products-label fl-left">
                            {i18n.t('components.filterBar.products', { count: this.props.totalProducts })}
                        </div>
                    ) : null}
                    {this.props.totalProducts ? (
                        <span className={classNames({ hidden: this.props.hideSelectButtons })}>
                            <a onClick={toggleSelectionModeFunc} className="fl-left cold-bgreen">
                                {' '}
                                <div style={{ display: 'inline-block' }}>
                                    {isInSelectionMode(this.props.productSelection)
                                        ? i18n.t('cancel')
                                        : i18n.t('select')}
                                </div>
                            </a>
                            <a
                                onClick={selectAllFunc}
                                className={classNames('fl-left', 'cold-bgreen', {
                                    hidden: this.props.hideSelectAll
                                })}
                            >
                                <div style={{ display: 'inline-block' }}>{i18n.t('selectAll')}</div>
                            </a>
                        </span>
                    ) : null}

                    {this.props.onChangePrice && !isInSelectionMode(this.props.productSelection) ? (
                        <ChangePriceDialog onChange={this.props.onChangePrice} size={ButtonSize.LARGE} />
                    ) : null}
                </div>
            );
        }

        if (this.props.totalProducts === 0 && !this.props.loading) {
            return <div className={classNames('filter-bar', this.props.className)}>{leftColumn}</div>;
        }

        return (
            <div className={classNames('filter-bar', this.props.className)} data-displayname="FilterBar">
                {leftColumn}
                <div className="layout-column-33 fl-right">
                    <SortByDropDown
                        onSortChange={this.props.onSortChange}
                        current={this.props.sortBy}
                        sorting={this.props.sorting}
                        excludeSorting={this.props.excludeSorting}
                    />
                    {!this.props.hideDisplayType && (
                        <DisplayTypeDropDown
                            onChange={this.props.onDisplayChange}
                            displayType={this.props.displayType}
                            excludeDisplayTypes={this.props.excludeDisplayTypes}
                        />
                    )}
                    {!this.props.hideGroupBy && (
                        <GroupByDropDown
                            groups={this.props.groupByGroups}
                            /* consider extracting onGroupyBy into ConnectedFilterBar,
                               but has different implementations*/
                            onGroupBy={this.props.onGroupBy}
                            current={this.props.grouping}
                        />
                    )}
                </div>
                <div className="layout-column-33 fl-right">
                    <Pagination
                        totalItems={this.props.totalProducts}
                        onPageChange={this.props.onPageChange}
                        currentPage={this.props.currentPage}
                        pageSize={this.props.pageSize}
                        hidden={this.props.hidePagination}
                    />
                </div>
            </div>
        );
    }
}

(FilterBar as any).defaultProps = {
    leftColumnText: '',
    isConnected: false,
    excludeDisplayTypes: [],
    excludeSorting: []
};

export default FilterBar;
